import { Button, Layout } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Task } from "src/types/tasks";
import { TaskCard } from "src/components/utils/TaskCard";
import { CategoryTabs } from "src/components/utils/CategoryTabs";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import { useFetchTasks } from "src/hooks/useTasks";
import TasksMobileNavbar from "src/components/utils/TasksMobileNavbar";
import { logEvent } from "src/analytics/amplitude";
import { useTasks } from "src/context/tasks";
import { CustomTaskModal } from "src/components/utils/CustomTaskModal";

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

const TasksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 24px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    font-size: 20px;
    margin-bottom: 8px;
    color: #373744;
  }

  p {
    font-size: 14px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const CustomTaskButton = styled.button`
  padding: 6px 12px;
  background-color: white;
  color: var(--blue-300);
  border: 1px solid var(--blue-300);
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: #f0f7ff;
    border-color: var(--blue-400);
    color: var(--blue-400);
  }

  &:active {
    background-color: #e0f0ff;
  }

  &:focus-visible {
    outline: 2px solid var(--blue-300);
    outline-offset: 2px;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    padding: 14px 24px;
  }
`;

interface TasksProps {
  changeTaskPage: (page: number) => void;
}

export default function Tasks({ changeTaskPage }: TasksProps) {
  const { selectedTasks, setSelectedTasks } = useTasks();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [isCustomTaskModalOpen, setIsCustomTaskModalOpen] = useState(false);

  // Fetch tasks using custom hook
  const { tasks, loading, error } = useFetchTasks();

  const width = useResizeObserver();
  const isMobile = width <= 500;

  // Extract unique categories from tasks
  const categories = useMemo(() => {
    if (!tasks.length) return ["All"];
    const uniqueCategories = Array.from(
      new Set(tasks.map((task) => task.category.name))
    );
    return ["All", ...uniqueCategories.sort()];
  }, [tasks]);

  // Filter tasks by selected category
  const filteredTasks = useMemo(() => {
    if (!tasks.length) return [];
    if (selectedCategory === "All") return tasks;
    return tasks.filter((task) => task.category.name === selectedCategory);
  }, [tasks, selectedCategory]);

  const handleTaskToggle = useCallback(
    (task: Task) => {
      setSelectedTasks((prev) => {
        const isSelected = prev.some((t) => t.service_id === task.service_id);
        if (isSelected) {
          logEvent("User deselected task", {
            taskId: task.service_id,
            taskTitle: task.title,
          });
          return prev.filter((t) => t.service_id !== task.service_id);
        } else {
          logEvent("User selected task", {
            taskId: task.service_id,
            taskTitle: task.title,
          });
          return [...prev, task];
        }
      });
    },
    [setSelectedTasks]
  );

  const handleNext = useCallback(() => {
    if (selectedTasks.length === 0) {
      return;
    }
    logEvent("User clicked next on tasks page", {
      tasksCount: selectedTasks.length,
    });
    changeTaskPage(2);
  }, [selectedTasks, changeTaskPage]);

  if (loading) {
    return (
      <Layout className="step-layout mobile-step-layout">
        <LoadingContainer>
          <p>Loading available tasks...</p>
        </LoadingContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout className="step-layout mobile-step-layout">
        <EmptyState>
          <h3>Error loading tasks</h3>
          <p>{error}</p>
        </EmptyState>
      </Layout>
    );
  }

  return (
    <Layout className="step-layout mobile-step-layout">
      <HeaderContainer>
        <h1 className="semi-bold" style={{ margin: 0 }}>
          Select your tasks
        </h1>
        <CustomTaskButton
          onClick={() => {
            logEvent("Custom task button clicked");
            setIsCustomTaskModalOpen(true);
          }}
          aria-label="Request a custom task"
        >
          Request a custom task
        </CustomTaskButton>
      </HeaderContainer>

      <CategoryTabs
        categories={categories}
        selectedCategory={selectedCategory}
        onChange={setSelectedCategory}
      />

      {filteredTasks.length === 0 ? (
        <EmptyState>
          <h3>No tasks available</h3>
          <p>Please check back later or select a different category.</p>
        </EmptyState>
      ) : (
        <TasksGrid>
          {filteredTasks.map((task) => (
            <TaskCard
              key={task.service_id}
              task={task}
              isSelected={selectedTasks.some(
                (t) => t.service_id === task.service_id
              )}
              onToggle={handleTaskToggle}
            />
          ))}
        </TasksGrid>
      )}

      <br />
      <br />

      {isMobile ? (
        <TasksMobileNavbar
          nextAction={handleNext}
          selectedTasks={selectedTasks}
          nextText="Continue"
        />
      ) : (
        <Button
          onClick={handleNext}
          type="primary"
          className="next-button"
          style={{ marginBottom: "16px" }}
          disabled={selectedTasks.length === 0}
        >
          {selectedTasks.length > 0
            ? `Continue with ${selectedTasks.length} task${
                selectedTasks.length !== 1 ? "s" : ""
              }`
            : "Select tasks to continue"}
        </Button>
      )}

      <CustomTaskModal
        open={isCustomTaskModalOpen}
        onOpenChange={setIsCustomTaskModalOpen}
      />
    </Layout>
  );
}
